import { Alert, Box, Breadcrumbs, Button, SvgIcon, Toolbar, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useCompanyFeature } from 'utils/useCompanyFeature';
import renderFolderCell from 'components/@home/@documents/renderFolderCell';
import FolderDialog from 'components/@home/@documents/SharedDocuments/FolderDialog';
import UploadButton from 'components/@home/@documents/SharedDocuments/UploadButton';
import MirrorFolderDrawer from 'components/@home/@settings/Tabs/ConnectedDrivesSettings/MirrorFolderDrawer';
import Loading from 'components/common/Loading';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import FolderPlusOutlineIcon from 'mdi-react/FolderPlusOutlineIcon';
import CloudOutlineIcon from 'mdi-react/CloudOutlineIcon';

import { useListCloudMirrorsQuery } from 'store/app/entities/CloudMirrorSlice';
import { string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import documentModalActions from 'store/app/ui/modals/document/action';
import pictureModalActions from 'store/app/ui/modals/picture/action';
import videoModalActions from 'store/app/ui/modals/video/action';
import {
  useCreateSharedFileMutation,
  useLazyGetSharedFileInfoQuery,
  useListSharedFilesQuery,
} from 'store/app/entities/SharedFilesSlice';
import isCompanyAdminSelector from 'store/selectors/isCompanyAdmin';
import renderDateTimeCell from 'utils/renderDateTimeCell';
import renderPermissionsCell from 'utils/renderPermissionsCell';
import useActions from 'utils/useActions';
import useOpenClose from 'utils/useOpenClose';
import { getMessageType, MESSAGE_TYPES } from 'utils/getMessageType';

const SharedDocuments = ({ folder }) => {
  const [newFolderIsOpen, newFolderOpen, newFolderClose] = useOpenClose();
  const [mirrorDialogIsOpen, mirrorDialogOpen, mirrorDialogClose] = useOpenClose();
  const isMirrorFolderEnabled = useCompanyFeature('mirrorFolder');

  const { data: mirrorsData } = useListCloudMirrorsQuery(undefined, {
    skip: folder !== 'shared' || !isMirrorFolderEnabled,
  });
  const mirrorByExternalId = React.useMemo(() => {
    const mappings = mirrorsData?.mappings || [];
    return Object.fromEntries(mappings.map(m => [m.externalFolderId, m]));
  }, [mirrorsData]);

  const [perPage, setPerPage] = useState(0);
  const [sortModel, setSortModel] = useState(undefined);
  const [filterModel, setFilterModel] = useState(undefined);
  const [page, setPage] = useState(0);
  const params = useParams();
  const listFolderId = params.folderId ?? params.id;
  const pathFileId = params.fileId ?? null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isCompanyAdmin = useSelector(isCompanyAdminSelector);
  const [triggerSharedFileInfo] = useLazyGetSharedFileInfoQuery();
  const [documentModal, pictureModal, videoModal] = useActions([
    documentModalActions,
    pictureModalActions,
    videoModalActions,
  ]);

  const [createSharedFile, { isLoading: isLoadingCreate }] = useCreateSharedFileMutation();
  const search = filterModel?.quickFilterValues.join(' ');
  const { data: { data } = {}, isLoading } = useListSharedFilesQuery(
    {
      search,
      folder,
      page: page + 1,
      perPage,
      id: listFolderId,
      sortBy: sortModel?.[0]?.field || 'name',
      sort: sortModel?.[0]?.sort || 'asc',
    },
    { skip: !perPage },
  );

  const location = useLocation();

  const fileIdFromQuery = searchParams.get('file');
  const resolvedOpenFileId = pathFileId || fileIdFromQuery;
  useEffect(() => {
    if (!resolvedOpenFileId || folder !== 'shared') {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const payload = await triggerSharedFileInfo(resolvedOpenFileId).unwrap();
        if (cancelled) return;
        const sf = payload?.sharedFile;
        if (!sf || sf.kind !== 'Document') return;
        const type = getMessageType({ attachments: [sf] });
        if (type === MESSAGE_TYPES.IMAGE) {
          pictureModal.open(sf.httpLink);
        } else if (type === MESSAGE_TYPES.VIDEO) {
          videoModal.open(sf.httpLink);
        } else if (type !== MESSAGE_TYPES.AUDIO) {
          documentModal.open(sf.httpLink, null, sf.name, false, sf.meta?.mimeType);
        }
      } catch {
        /* no access or network — strip deep-link so refresh does not loop */
      } finally {
        if (!cancelled) {
          if (pathFileId && listFolderId != null) {
            navigate(`/home/documents/shared/${listFolderId}/`, { replace: true });
          } else if (fileIdFromQuery) {
            setSearchParams(
              prev => {
                const next = new URLSearchParams(prev);
                next.delete('file');
                return next;
              },
              { replace: true },
            );
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    fileIdFromQuery,
    folder,
    listFolderId,
    navigate,
    pathFileId,
    resolvedOpenFileId,
    triggerSharedFileInfo,
    documentModal,
    pictureModal,
    videoModal,
    setSearchParams,
  ]);

  const rows = data?.sharedFiles || [];
  const columns = [
    {
      field: 'name',
      flex: 1,
      editable: false,
      renderCell: renderFolderCell(
        folder,
        {
          search: [...(location.state?.search || []), location.search],
        },
        'shared',
        mirrorByExternalId,
      ),
      headerName: I18n.t('DocumentsScene.DocumentsTable.Name'),
    },
    {
      field: 'createdAt',
      type: 'string',
      flex: 1,
      editable: false,
      maxWidth: 200,
      renderCell: renderDateTimeCell,
      headerName: I18n.t('DocumentsScene.DocumentsTable.CreatedAt'),
    },
    ...(folder === 'shared'
      ? [
          {
            field: 'permissions',
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: renderPermissionsCell,
            headerName: I18n.t('DocumentsScene.DocumentsTable.SharedWith'),
          },
        ]
      : []),
  ];
  const handleNewFolderSubmit = async name => {
    await createSharedFile({
      kind: 'Folder',
      name,
      parent: data?.parent?._id,
      permissions: data?.parent?.permissions.map(p => ({ team: p.team, role: p.role })) || [],
    });
  };
  return (
    <Grid sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', maxHeight: '100vh' }}>
      {folder !== 'deleted' &&
        !data?.parent?.source?.readOnly &&
        ((!data?.parent && isCompanyAdmin) || data?.parent?.isAdmin) && (
          <Toolbar
            sx={{
              minHeight: '0 !important',
              overflow: 'hidden',
              borderBottom: '1px solid',
              borderColor: 'divider',
              '& .MuiButton-root': {
                borderRadius: 0,
                minHeight: 49,
                whiteSpace: 'nowrap',
                '& .MuiCircularProgress-root': {
                  marginLeft: 1,
                },
              },
            }}
          >
            <Button variant="text" color="secondary" onClick={newFolderOpen}>
              <SvgIcon
                sx={{
                  fontSize: 18,
                  marginRight: 1,
                  color: 'primary.main',
                }}
              >
                <FolderPlusOutlineIcon />
              </SvgIcon>
              {I18n.t('DocumentsScene.New folder')}
              {isLoadingCreate && <Loading size={15} />}
            </Button>
            <UploadButton folder={folder} id={listFolderId} />
            {isMirrorFolderEnabled && !data?.parent && (
              <Button variant="text" color="secondary" onClick={mirrorDialogOpen}>
                <SvgIcon sx={{ fontSize: 18, marginRight: 1, color: 'primary.main' }}>
                  <CloudOutlineIcon />
                </SvgIcon>
                {I18n.t('DocumentsScene.Mirror folder')}
              </Button>
            )}
          </Toolbar>
        )}
      <Box
        sx={{
          padding: 2,
          paddingBottom: 0,
          flex: 1,
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'column',
        }}
      >
        {data?.parent ? (
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              to={{
                pathname: `/home/documents/${folder}/`,
                search: location.state?.search?.[0] || '',
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 'normal' }}>
                {I18n.t(`DocumentsScene.Folders.${folder}`)}
              </Typography>
            </Link>
            {data.parent.ancestors.map((ancestor, idx) => (
              <Link
                key={ancestor._id}
                underline="hover"
                color="inherit"
                to={{
                  pathname: `/home/documents/${folder}/${ancestor._id}/`,
                  search: location.state?.search?.[idx + 1] || '',
                }}
                state={{ search: location.state?.search?.slice(0, -1) }}
              >
                <Typography variant="h3" sx={{ fontWeight: 'normal' }}>
                  {ancestor.name}
                </Typography>
              </Link>
            ))}
            <Typography variant="h3">{data.parent.name}</Typography>
          </Breadcrumbs>
        ) : (
          <Typography variant="h3"> {I18n.t(`DocumentsScene.Folders.${folder}`)}</Typography>
        )}
        {folder === 'deleted' && (
          <Alert severity="info" sx={{ marginTop: 2, marginBottom: 1 }}>
            {I18n.t('DocumentsScene.Items in the trash')}
          </Alert>
        )}
        <OmmnioDataGrid
          loading={!perPage || (isLoading && rows.length === 0)}
          sx={{ marginTop: 2, flex: 1, minHeight: 320 }}
          columns={columns}
          rows={rows}
          rowCount={data?.totalCount || 0}
          paginationMode="server"
          getRowId={r => r._id}
          sortingMode="server"
          filterMode="server"
          page={page}
          pageSize={perPage}
          resetPaginationKey={`${folder}-${listFolderId ?? ''}`}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          onSortModelChange={setSortModel}
          onPageChange={setPage}
          onPageSizeChange={setPerPage}
          updateHistory
          disableEvenOdd
        />
      </Box>
      <FolderDialog
        edit
        onSubmit={handleNewFolderSubmit}
        onClose={newFolderClose}
        open={newFolderIsOpen}
      />
      <MirrorFolderDrawer
        open={isMirrorFolderEnabled && mirrorDialogIsOpen}
        onClose={mirrorDialogClose}
      />
    </Grid>
  );
};

SharedDocuments.propTypes = {
  folder: string.isRequired,
};

export default SharedDocuments;
